import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Headphones, Award, Wrench, Zap, Star, BadgeCheck, Quote, Gauge, Thermometer, Settings2, Sparkles, Clock, Camera, Tag, Ghost } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import PromoBanner from "@/components/PromoBanner";
import RecentlyViewed from "@/components/RecentlyViewed";
import Seo from "@/components/Seo";

const HERO = "/hero-controls.jpg";
const TRADE = "https://images.unsplash.com/photo-1518276779712-dfdcb9daa7a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const FLOORTEC = "https://customer-assets-rejwkqb3.emergentagent.net/job_bronte-trade-hub/artifacts/eok10gq7_image.png";

const COMING_SOON = [
  {
    name: "Unisenza Plus Thermostat RF – 2 Channel",
    desc: "Wireless programmable room thermostat with backlit display for heating and hot water.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_bronte-trade-hub/artifacts/sl7d6y4h_image.png",
  },
  {
    name: "Unisenza Plus Gateway",
    desc: "Wi-Fi & ZigBee smart hub connecting your Unisenza Plus devices to the app.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_bronte-trade-hub/artifacts/pofqn0v2_image.png",
  },
  {
    name: "Unisenza Plus Receiver RF – 2 Channel",
    desc: "Dual-channel wiring centre / receiver for switching heating and hot water.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_bronte-trade-hub/artifacts/1nxsii6y_image.png",
  },
];

const BRANDS = ["MYSON", "DANFOSS", "DRAYTON", "EPH CONTROLS", "HYDRONIC"];

const FAQS = [
  ["Are your Myson parts genuine?", "Yes. We only supply genuine, fully warranted Myson heating controls, valves and spares sourced through official channels — never unbranded copies."],
  ["Do you offer free UK delivery?", "Yes — free tracked delivery (Royal Mail Tracked 48, aim to arrive within 2–3 working days) on all in-stock products across mainland UK, with no minimum order."],
  ["How quickly are orders dispatched?", "In-stock orders are dispatched as quickly as possible and sent free by Royal Mail Tracked 48, which aims to arrive within 2–3 working days of dispatch."],
  ["Can I order as a trade customer or in bulk?", "Absolutely. Installers and merchants can request tailored trade pricing using the 'Request a trade quote' button on any category page, or by contacting us directly."],
  ["What if I can't find the product I need?", "Send us a message with the product details and we'll do our best to source it and get you a quote — we supply far more than is listed online."],
  ["What size TRV or radiator valve do I need — 10mm or 15mm?", "Most UK domestic radiators use 15mm valves, while microbore heating systems use 10mm. Check the outside diameter of the copper pipe running to your radiator, or measure the existing valve's compression nut. If you're unsure, send us a photo and we'll help you identify the right size."],
  ["What's the difference between compression and pushfit valves?", "Compression valves seal onto copper pipe with a brass olive and nut and are the traditional installer's choice. Pushfit valves simply push onto the pipe for a tool-free connection, which is quicker for DIY. Both are reliable when fitted correctly — choose whichever suits your pipework and confidence level."],
  ["What is your returns policy?", "We offer a 30-day returns period from delivery. Items should be unused, complete with accessories and in their original packaging where reasonably possible. There is no restocking fee, and your statutory rights are unaffected."],
];

const POPULAR_CATS = [
  { slug: "myson-trvs", label: "TRVs", sub: "Radiator valves", icon: Gauge },
  { slug: "myson-thermostats", label: "Thermostats", sub: "Heating controls", icon: Thermometer },
  { slug: "myson-mpe-valves", label: "Motorised Valves", sub: "Zone valves", icon: Settings2 },
  { slug: "bleed-vents", label: "Myson Spares", sub: "Parts & accessories", icon: Wrench },
];

export default function Home() {
  const [collections, setCollections] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [bargains, setBargains] = useState([]);

  // Halloween theme: auto-on 24–31 Oct (reverts 1 Nov). Preview with ?halloween=1 / force off with ?halloween=0
  const isHalloween = (() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("halloween");
    if (flag === "1") return true;
    if (flag === "0") return false;
    const now = new Date();
    return now.getMonth() === 9 && now.getDate() >= 24 && now.getDate() <= 31;
  })();

  const scrollToShop = () => document.getElementById("shop-categories")?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data));
    api.get("/reviews/featured?limit=8").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/products/bestsellers?limit=5").then((r) => setBestsellers(r.data)).catch(() => {});
    api.get("/products", { params: { collection: "bargain-bin" } }).then((r) => setBargains(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Heating Controls, TRVs, Motorised Valves & Genuine Myson Parts"
        description="Shop genuine Myson heating controls, thermostatic radiator valves, motorised valves, thermostats and heating spares. Free tracked UK delivery (Royal Mail Tracked 48) and fast dispatch."
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Hydronic Heating Controls & Valves",
          alternateName: "HHCV",
          url: "https://hhcv.co.uk",
        }, {
          "@context": "https://schema.org",
          "@type": "Store",
          name: "Hydronic Heating Controls & Valves",
          alternateName: "HHCV",
          url: "https://hhcv.co.uk",
          image: HERO,
          description:
            "Family-owned UK supplier of central heating controls and valves — genuine Myson TRVs, thermostats, MPE valves, kickspace heaters and spares with free tracked UK delivery (Royal Mail Tracked 48).",
          telephone: "+44 7443 173458",
          email: "enquiries@hhcv.co.uk",
          priceRange: "££",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Rear Heron Drive",
            addressLocality: "Darlington",
            postalCode: "DL1 1DL",
            addressRegion: "County Durham",
            addressCountry: "GB",
          },
          areaServed: { "@type": "Country", name: "United Kingdom" },
          openingHours: "Mo-Fr 08:00-17:00",
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }]}
      />
      <PromoBanner />
      {isHalloween && (
        <div className="relative overflow-hidden bg-[#1a1026] border-b border-orange-500/30" data-testid="halloween-banner">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 15% 50%, #f97316 0, transparent 40%), radial-gradient(circle at 85% 50%, #f97316 0, transparent 40%)" }} />
          <div className="relative max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-center">
            <Ghost className="w-4 h-4 text-orange-400 shrink-0 animate-pulse" />
            <p className="text-sm font-semibold text-orange-50">
              <span className="text-orange-400">Spooky-good savings this Halloween</span>
              <span className="hidden sm:inline text-orange-100/80"> — genuine heating controls, no tricks. Free tracked delivery on every order.</span>
            </p>
            <Ghost className="w-4 h-4 text-orange-400 shrink-0 animate-pulse" />
          </div>
        </div>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-slate-100" data-testid="hero">
        <img
          src={HERO}
          alt="Myson heating controls range — central heating programmer, room thermostat, motorised zone valves and thermostatic radiator valve in a modern living room"
          className="absolute inset-0 w-full h-full object-cover object-right-bottom scale-[1.06] origin-bottom-right"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/55 md:from-white/90 md:via-white/55 md:to-transparent" />
        {isHalloween && (
          <>
            <Ghost className="absolute top-8 right-10 w-10 h-10 text-orange-500/25 animate-float hidden md:block" data-testid="hero-halloween-motif" />
            <Ghost className="absolute bottom-10 right-28 w-6 h-6 text-orange-500/20 animate-float hidden lg:block" style={{ animationDelay: "1.2s" }} />
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-4 py-16 lg:py-28 min-h-[460px] lg:min-h-[560px] flex items-center">
          <div className="max-w-xl animate-fade-up">
            <span className={`text-overline ${isHalloween ? "text-orange-600" : "text-primary"}`}>{isHalloween ? "Spooky-season heating deals 🎃" : "UK supplier of heating controls & spares"}</span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter leading-[1.03]">
              UK Heating Controls, Thermostatic Radiator Valves &amp; Genuine Myson Spare Parts
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed">
              HHCV supplies genuine Myson heating controls, <Link to="/collections/myson-trvs" className="text-primary underline-offset-2 hover:underline">thermostatic radiator valves</Link>, <Link to="/collections/myson-thermostats" className="text-primary underline-offset-2 hover:underline">room thermostats</Link> and <Link to="/collections/myson-mpe-valves" className="text-primary underline-offset-2 hover:underline">motorised valves</Link> throughout the UK. Fast dispatch, competitive prices and free tracked UK delivery (Royal Mail Tracked 48).
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Genuine <span className="font-semibold text-secondary">Myson</span>, <span className="font-semibold text-secondary">Danfoss</span>, <span className="font-semibold text-secondary">Drayton</span> &amp; <span className="font-semibold text-secondary">EPH</span> heating spares.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={scrollToShop} data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 rounded-sm font-semibold hover:-translate-y-0.5 hover:shadow-hard transition-all">
                Shop all products <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to={`/contact?subject=${encodeURIComponent("Help finding a replacement part")}&message=${encodeURIComponent("Hi, I'm trying to find a replacement part. Here are the details (brand / model number / photo if possible):\n")}`}
                data-testid="hero-part-btn"
                className="inline-flex items-center gap-2 border border-secondary/30 bg-white px-7 py-3.5 rounded-sm font-semibold hover:border-primary transition-colors">
                Find a replacement part
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span className="flex items-center gap-2 font-medium"><ShieldCheck className="w-4 h-4 text-primary" /> Genuine parts</span>
              <span className="flex items-center gap-2 font-medium"><Truck className="w-4 h-4 text-primary" /> Free tracked delivery</span>
              <span className="flex items-center gap-2 font-medium"><Headphones className="w-4 h-4 text-primary" /> UK technical support</span>
              <span className="flex items-center gap-2 font-medium"><Award className="w-4 h-4 text-primary" /> Trusted heating supplier</span>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR CATEGORY TILES */}
      <section id="shop-categories" className="scroll-mt-24 border-b border-border bg-white" data-testid="popular-categories">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {POPULAR_CATS.map((c) => (
              <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`quick-cat-${c.slug}`}
                className="group flex items-center gap-3 border border-border rounded-sm p-4 hover:border-primary hover:shadow-hard transition-all">
                <span className="grid place-items-center w-11 h-11 rounded-sm bg-primary/10 text-primary shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                  <c.icon className="w-5 h-5" />
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold text-sm leading-tight">{c.label}</span>
                  <span className="text-xs text-muted-foreground">{c.sub}</span>
                </span>
                <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FIND MY REPLACEMENT */}
      <section className="border-b border-border bg-primary text-white" data-testid="find-replacement-band">
        <div className="max-w-7xl mx-auto px-4 py-8 lg:py-10 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="shrink-0 grid place-items-center w-14 h-14 rounded-sm bg-white/15">
            <Camera className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-display font-bold tracking-tight">Replacing an old heating control?</h2>
            <p className="mt-1.5 text-white/80 leading-relaxed max-w-2xl">
              Not sure of the part number? Send us a photo or the details of your old valve, thermostat or controller and we'll identify the correct replacement for you.
            </p>
          </div>
          <Link
            to="/find-replacement"
            data-testid="find-replacement-btn"
            className="shrink-0 inline-flex items-center gap-2 bg-accent text-white px-7 py-3.5 rounded-sm font-semibold hover:-translate-y-0.5 hover:shadow-hard transition-all">
            Find My Replacement <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-border bg-white" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 py-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
          {[
            { icon: Award, t: "Genuine Myson Parts" },
            { icon: Truck, t: "Free Tracked Delivery" },
            { icon: Zap, t: "Fast Dispatch" },
            { icon: Headphones, t: "Expert Technical Support" },
            { icon: ShieldCheck, t: "Secure Checkout" },
          ].map((x) => (
            <div key={x.t} className="flex items-center gap-2 font-medium">
              <x.icon className="w-5 h-5 text-primary shrink-0" /> {x.t}
            </div>
          ))}
        </div>
      </section>

      {/* BRAND MARQUEE */}
      <section className="steel-texture text-white py-5 overflow-hidden">
        <div className="flex gap-16 animate-marquee whitespace-nowrap w-max">
          {[...BRANDS, ...BRANDS].map((b, i) => (
            <span key={`${b}-${i}`} className="font-display font-bold text-lg text-white/50 tracking-widest">{b}</span>
          ))}
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-overline text-primary">Shop by category</span>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Featured Collections</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {collections.map((c, i) => (
            <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`collection-card-${c.slug}`}
              className={`group relative overflow-hidden rounded-sm border border-border ${i === 0 ? "lg:col-span-2 lg:row-span-1" : ""}`}>
              <div className={`${i === 0 ? "aspect-[16/9]" : "aspect-[4/3]"} overflow-hidden bg-white`}>
                <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <span className="text-overline text-accent">{c.tagline}</span>
                <h3 className="text-white font-display font-bold text-xl mt-1 flex items-center gap-2">
                  {c.name} <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* COMING SOON — UNISENZA PLUS */}
      <section className="border-y border-border bg-secondary text-white overflow-hidden" data-testid="coming-soon-section">
        <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
            <div>
              <span className="inline-flex items-center gap-2 text-overline text-accent">
                <Sparkles className="w-4 h-4" /> Coming soon
              </span>
              <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">
                Unisenza Plus — Smart Heating Control
              </h2>
              <p className="mt-3 text-white/70 max-w-2xl leading-relaxed">
                A new range of connected, app-controlled heating products landing soon at HHCV. Register your interest and we'll let you know the moment they're available.
              </p>
            </div>
            <Link
              to={`/contact?subject=${encodeURIComponent("Unisenza Plus — register interest")}&message=${encodeURIComponent("Hi, please notify me when the Unisenza Plus range is available:\n- Unisenza Plus Thermostat RF – 2 Channel\n- Unisenza Plus Gateway\n- Unisenza Plus Receiver RF – 2 Channel\n")}`}
              data-testid="coming-soon-notify-btn"
              className="shrink-0 inline-flex items-center gap-2 bg-accent text-white px-7 py-3.5 rounded-sm font-semibold hover:-translate-y-0.5 hover:shadow-hard transition-all">
              Register interest <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
            {COMING_SOON.map((p) => (
              <div key={p.name} data-testid={`coming-soon-card-${p.name}`}
                className="group relative rounded-sm border border-white/15 bg-white/[0.04] overflow-hidden hover:border-accent/60 transition-colors">
                <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-accent text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
                  <Clock className="w-3.5 h-3.5" /> Coming soon
                </div>
                <div className="aspect-square bg-white overflow-hidden">
                  <img src={p.image} alt={p.name} loading="lazy"
                    className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-base leading-snug">{p.name}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* HOW IT WORKS */}
          <div className="mt-8 lg:mt-10 rounded-sm border border-white/15 bg-white/[0.04] p-6 lg:p-10" data-testid="unisenza-how-it-works">
            <h3 className="font-display font-bold text-xl lg:text-2xl tracking-tight">How the Unisenza Plus System Works</h3>
            <p className="mt-3 text-white/70 leading-relaxed max-w-3xl">
              The Unisenza Plus Gateway connects your heating controls to the Unisenza Plus app, allowing convenient control from your smartphone.
            </p>

            {/* Flow diagram */}
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              {["Thermostat", "Gateway", "RF Receiver", "Heating & Hot Water"].map((step, i, arr) => (
                <div key={step} className="flex items-center gap-2 sm:gap-3">
                  <span className="inline-flex items-center rounded-sm bg-white/10 border border-white/15 px-3.5 py-2 text-sm font-semibold whitespace-nowrap">
                    {step}
                  </span>
                  {i < arr.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-accent shrink-0 rotate-90 sm:rotate-0" />
                  )}
                </div>
              ))}
            </div>

            <ul className="mt-7 grid gap-3 sm:grid-cols-3">
              {[
                ["2-Channel Thermostat", "Controls room temperature and hot water schedules."],
                ["Gateway", "Connects the system to the Unisenza Plus app."],
                ["RF Receiver", "Controls the boiler, heating and hot water."],
              ].map(([t, d]) => (
                <li key={t} className="rounded-sm border border-white/10 bg-white/[0.03] p-4">
                  <span className="flex items-center gap-2 font-semibold text-sm">
                    <BadgeCheck className="w-4 h-4 text-accent shrink-0" /> {t}
                  </span>
                  <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{d}</p>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-white/70 leading-relaxed max-w-3xl">
              Together, they provide simple smart control of your heating and hot water — from home or remotely.
            </p>
          </div>
        </div>
      </section>

      {/* UNDERFLOOR HEATING */}
      <section className="border-y border-border bg-white" data-testid="underfloor-heating-section">
        <div className="max-w-7xl mx-auto px-4 py-14 lg:py-20">
          <div className="rounded-sm overflow-hidden border border-border shadow-hard">
            <img src={FLOORTEC} alt="Myson Floortec Underfloor Punch Pack — complete single-room underfloor heating kit including multi-layer pipe, tacker nails, single zone unit and touch-screen thermostat" className="w-full h-auto" />
          </div>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-muted-foreground max-w-2xl">
              Complete Myson Floortec underfloor heating kits and components, tailored to your room sizes and system. Available on request — tell us about your project and our specialists will put together the right package for you.
            </p>
            <Link to="/contact?subject=Underfloor%20Heating%20enquiry" data-testid="underfloor-enquire-btn"
              className="shrink-0 inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 rounded-sm font-semibold hover:-translate-y-0.5 hover:shadow-hard transition-all">
              Enquire about Underfloor Heating <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-y border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Truck, t: "Free Tracked Delivery", d: "Free Royal Mail Tracked 48 on every order, aiming to arrive within 2–3 working days." },
            { icon: ShieldCheck, t: "Genuine Parts", d: "Only authentic manufacturer products, fully warranted." },
            { icon: Wrench, t: "Everything You Need", d: "Valves, controls and spares in one convenient place." },
            { icon: Headphones, t: "Expert Support", d: "Talk to real heating specialists, Mon–Fri." },
          ].map((b) => (
            <div key={b.t} className="flex gap-4">
              <div className="shrink-0 grid place-items-center w-12 h-12 rounded-sm bg-primary/10 text-primary"><b.icon className="w-6 h-6" /></div>
              <div>
                <h3 className="font-semibold">{b.t}</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MOST POPULAR (best sellers) */}
      {bestsellers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-16 lg:py-24" data-testid="bestsellers-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-overline text-primary">Popular</span>
              <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Popular Heating Spares</h2>
            </div>
            <Link to="/collections/myson-trvs" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
            {bestsellers.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* BARGAIN BIN */}
      {bargains.length > 0 && (
        <section className="border-y border-border bg-amber-50/60" data-testid="bargain-bin-section">
          <div className="max-w-7xl mx-auto px-4 py-16 lg:py-20">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="inline-flex items-center gap-2 text-overline text-amber-700"><Tag className="w-4 h-4" /> Bargain Bin</span>
                <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Bargain Bin</h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                  <span className="font-bold text-foreground bg-amber-200/70 px-1 rounded-sm">New stock</span> with <span className="font-bold text-foreground bg-amber-200/70 px-1 rounded-sm">damaged packaging</span>, sold off cheap. Fully working, never used and warranted — limited quantities.
                </p>
              </div>
              <Link to="/collections/bargain-bin" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:gap-2.5 transition-all">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {bargains.slice(0, 3).map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="border-y border-border bg-muted/40" data-testid="testimonials-strip">
          <div className="max-w-7xl mx-auto px-4 py-16 lg:py-20">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="text-overline text-primary">What customers say</span>
                <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Trusted by homeowners &amp; installers</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.slice(0, 6).map((t) => (
                <figure key={t.id} data-testid={`testimonial-${t.id}`} className="bg-white border border-border rounded-sm p-6 flex flex-col">
                  <Quote className="w-6 h-6 text-primary/30" />
                  <div className="mt-3 flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= t.rating ? "fill-accent text-accent" : "text-muted-foreground/25"}`} />
                    ))}
                  </div>
                  {t.title && <figcaption className="mt-3 font-semibold text-sm">{t.title}</figcaption>}
                  <blockquote className="mt-1.5 text-sm text-muted-foreground leading-relaxed line-clamp-5 flex-1">{t.body}</blockquote>
                  <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium flex items-center gap-1.5">
                      {t.name}
                      {t.verified && <span className="inline-flex items-center gap-1 text-primary"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>}
                    </span>
                    {t.product_slug && (
                      <Link to={`/products/${t.product_slug}`} className="text-xs font-semibold text-primary hover:underline shrink-0">View product</Link>
                    )}
                  </div>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRODUCT REQUEST BAND */}
      <section className="max-w-7xl mx-auto px-4 pb-4" data-testid="product-request-band">
        <div className="rounded-sm border border-border bg-card p-8 lg:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div className="max-w-2xl">
            <span className="text-overline text-primary">Can't find it?</span>
            <h2 className="mt-2 text-xl sm:text-2xl font-display font-bold tracking-tight">Can't find the part anywhere?</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              We supply thousands of heating controls and spares that aren't listed online. Send us the manufacturer, part number or even a photo and we'll try to identify and source it for you.
            </p>
          </div>
          <Link
            to={`/contact?subject=${encodeURIComponent("Product Request / Quote")}&message=${encodeURIComponent("Hi, I'm looking for the following product(s) — please can you source and quote:\n\n")}`}
            data-testid="product-request-btn"
            className="shrink-0 inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 rounded-sm font-semibold hover:-translate-y-0.5 hover:shadow-hard transition-all">
            Ask HHCV to Find It <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* SUPPORT CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-16 lg:pb-24">
        <div className="relative overflow-hidden rounded-sm border border-border steel-texture text-white">
          <img src={TRADE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          <div className="relative p-10 lg:p-16 max-w-2xl">
            <Zap className="w-8 h-8 text-accent" />
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Not sure which part you need?</h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              Our heating specialists are on hand to help you find the right TRV, thermostat or valve for your system. Genuine parts, fast dispatch and honest advice — Mon to Fri.
            </p>
            <Link to="/contact" data-testid="trade-cta-btn"
              className="mt-7 inline-flex items-center gap-2 bg-accent text-white px-7 py-3.5 rounded-sm font-semibold hover:-translate-y-0.5 transition-transform">
              Talk to an Expert <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO CONTENT */}
      <section className="border-t border-border bg-white" data-testid="home-seo-content">
        <div className="max-w-4xl mx-auto px-4 py-14 lg:py-20 space-y-8">
          <div>
            <span className="text-overline text-primary">About HHCV</span>
            <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold tracking-tight">Your UK specialist for heating controls &amp; genuine Myson spares</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Hydronic Heating Controls and Valves (HHCV) is a UK supplier of central heating controls, radiator valves and genuine Myson components for homeowners, landlords, installers and merchants. Whether you're upgrading a single radiator or fitting out an entire heating system, we help you find the correct replacement part quickly — with honest advice, competitive prices and free tracked UK delivery (Royal Mail Tracked 48) on every order. We supply leading heating brands including Myson, Danfoss, Drayton and EPH Controls.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-display font-bold">Genuine Myson specialists</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              We specialise in genuine Myson heating controls and spares — from <Link to="/collections/myson-trvs" className="text-primary underline-offset-2 hover:underline">TRV2 thermostatic radiator valves</Link> and manual valves to MPE motorised valves, room thermostats, Kickspace fan convectors and Floortec underfloor heating. Because we focus on authentic, fully warranted parts sourced through official channels — never unbranded copies — the component you fit is the one that's designed for your system and built to last. If you're replacing a like-for-like Myson part, we'll help you identify the exact match.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-display font-bold">Thermostatic radiator valves (TRVs)</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              A thermostatic radiator valve (TRV) automatically controls the temperature of an individual radiator, saving energy by only heating rooms to the level you need. We stock genuine <Link to="/collections/myson-trvs" className="text-primary underline-offset-2 hover:underline">Myson TRV2 thermostatic radiator valves</Link> in 10mm and 15mm sizes — including angled, straight and pushfit options — plus complete radiator packs with matched lockshields. New to TRVs? Read our guide on <Link to="/guides/how-does-a-trv-work" className="text-primary underline-offset-2 hover:underline">how a TRV works</Link>.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-display font-bold">Heating controls &amp; thermostats</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              From simple dial room thermostats to programmable and smart wireless (RF) controls, the right heating control keeps your home comfortable and efficient. Browse our <Link to="/collections/myson-thermostats" className="text-primary underline-offset-2 hover:underline">electronic heating controls and thermostats</Link> from trusted brands including Myson, Danfoss, Drayton and EPH, with OpenTherm-compatible options for modern combi and system boilers. Not sure which to pick? See our guide to <Link to="/guides/choosing-the-right-room-thermostat" className="text-primary underline-offset-2 hover:underline">choosing the right room thermostat</Link>.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-display font-bold">Motorised valves, manual valves &amp; central heating parts</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Motorised zone valves control the flow of hot water to your heating and hot water circuits in S-plan and Y-plan systems. Explore our <Link to="/collections/myson-mpe-valves" className="text-primary underline-offset-2 hover:underline">Myson MPE motorised valves</Link>, <Link to="/collections/myson-manual-radiator-valves" className="text-primary underline-offset-2 hover:underline">manual radiator valves and lockshields</Link>, <Link to="/collections/kickspace-grills" className="text-primary underline-offset-2 hover:underline">Kickspace fan convectors</Link> and radiator <Link to="/collections/bleed-vents" className="text-primary underline-offset-2 hover:underline">spares and accessories</Link> — all genuine parts.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-display font-bold">Heating controls for homeowners &amp; installers</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              We supply both homeowners tackling a single repair and installers and merchants buying in volume. Homeowners get plain-English product descriptions, help identifying the right replacement part and free tracked delivery on every order. Installers and trade customers can request tailored trade pricing and bulk quotes on any category page using the <Link to="/contact" className="text-primary underline-offset-2 hover:underline">trade quote</Link> option — ideal for stocking up or pricing a project. Whatever the job, our team is happy to help you get the correct part first time.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-display font-bold">Free tracked UK delivery</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Every order ships free across mainland UK by Royal Mail Tracked 48 (aim to arrive within 2–3 working days), with no minimum spend. Checkout is secure, and you can order as a guest or create an account to track your orders.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-display font-bold">Why choose HHCV?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              As specialist heating controls suppliers, we focus on genuine, fully warranted components rather than cheap unbranded alternatives — so the part you fit is the part that lasts. With over 80 years' combined experience across our team, we help homeowners, installers and merchants source the correct replacement part quickly, backed by real technical support from people who know heating. Can't find what you need? <Link to="/contact" className="text-primary underline-offset-2 hover:underline">Send us a message</Link> with the product and we'll do our best to source it and quote — we supply far more than is listed online.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border" data-testid="home-faq">
        <div className="max-w-4xl mx-auto px-4 py-14 lg:py-20">
          <span className="text-overline text-primary">Good to know</span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold tracking-tight">Frequently Asked Questions</h2>
          <div className="mt-8 divide-y divide-border border-y border-border">
            {FAQS.map(([q, a], i) => (
              <details key={q} className="group py-4" data-testid={`faq-item-${i}`} {...(i === 0 ? { open: true } : {})}>
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-semibold text-[15px] hover:text-primary transition-colors">
                  {q}
                  <ArrowRight className="w-4 h-4 shrink-0 text-primary transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed text-sm">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <RecentlyViewed />
    </div>
  );
}
